import { useRef, useEffect, useState } from 'react';
import { useChatContext } from '@/contexts/ChatContext';
import { UserMessage, AgentMessage } from '@/components/chat/MessageItem';
import { AnimatePresence, motion } from 'framer-motion';
import ChatInput from '@/components/chat/ChatInput';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  Terminal,
  FileArrowUp,
  FileText,
  ChartBar,
  Code,
  CaretCircleRight
} from 'phosphor-react';

interface SideChatProps {
  isOpen: boolean;
  onClose: () => void;
  docTitle?: string;
}

const EXAMPLE_ACTIONS = [
  { icon: <FileText className="h-4 w-4" />, text: 'Criar um PRD completo' },
  { icon: <Code className="h-4 w-4" />, text: 'Analisar arquitetura de software' },
  { icon: <ChartBar className="h-4 w-4" />, text: 'Gerar relatório de métricas' },
  { icon: <FileArrowUp className="h-4 w-4" />, text: 'Criar proposta comercial' },
  { icon: <Terminal className="h-4 w-4" />, text: 'Pesquisar tecnologias' },
];

const getAgentIconKey = (name: string): 'pen' | 'image' | 'search' | 'doc' | 'chart' | 'code' => {
  const n = (name || '').toLowerCase();
  if (n.includes('montfort')) return 'pen';
  if (n.includes('roussillon') || n.includes('proposta') || n.includes('document')) return 'doc';
  if (n.includes('otto') || n.includes('code') || n.includes('dev')) return 'code';
  if (n.includes('samson') || n.includes('search')) return 'search';
  if (n.includes('gerier') || n.includes('chart') || n.includes('métric') || n.includes('metric')) return 'chart';
  if (n.includes('engelier') || n.includes('image') || n.includes('design')) return 'image';
  return 'pen';
};

export function SideChat({ isOpen, onClose, docTitle }: SideChatProps) {
  const { messages, getCurrentAgent, getCurrentChat, isStreaming, isLoading, startTypingAnimation, stopTypingAnimation } = useChatContext();
  const agent = getCurrentAgent();
  const chat = getCurrentChat();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isMinimized] = useState(false);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (isOpen && !isMinimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isStreaming, isOpen, isMinimized]);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30,
        mass: 0.8
      }}
      className="fixed right-0 top-0 bottom-0 w-[420px] max-w-[90vw] z-30 flex flex-col bg-[color:var(--background)] border-l theme-border shadow-2xl max-md:w-full max-md:max-w-full"
    >
      {/* Toggle Button - Matches Sidebar style */}
      <button
        onClick={onClose}
        className="absolute -left-3 top-4.5 z-20 bg-white dark:bg-[#18181b] theme-border border rounded-full p-0 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-all shadow-lg hover:shadow-xl"
        aria-label="Fechar chat"
        title="Fechar chat"
      >
        <CaretCircleRight
          className="w-6 h-6 text-[color:var(--nt-primary)] transition-transform duration-300"
          weight="fill"
        />
      </button>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b theme-border bg-[color:var(--background)]">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[color:var(--nt-primary)]/10 flex items-center justify-center">
            {agent?.name ? (
              <img
                src={`/icons/feature/${getAgentIconKey(agent.name)}.svg`}
                alt=""
                width={20}
                height={20}
                className="block"
              />
            ) : (
              <svg className="w-5 h-5 text-[color:var(--nt-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold theme-text truncate">
              {chat?.title || 'Nova conversa'}
            </div>
            {docTitle && (
              <div className="text-xs theme-text-muted truncate">
                {docTitle}
              </div>
            )}
          </div>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages Area */}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden relative">
            {/* Soft edge fades */}
            <div className="pointer-events-none sticky top-0 z-10 h-10 bg-gradient-to-b from-[color:var(--background)] to-transparent" />

            {isLoading ? (
              <div className="h-full flex items-center justify-center">
                <LoadingSpinner size="lg" />
              </div>
            ) : messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center px-3 py-6 overflow-y-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="w-full max-w-full mx-auto flex flex-col"
                >
                  {/* Header Section */}
                  <div className="mb-6 text-center">
                    <div className="flex items-center justify-center mb-4">
                      {/* Animated logo with liquid metal effect */}
                      <motion.div
                        className="relative flex items-center justify-center"
                        animate={{
                          rotate: [0, 360],
                        }}
                        transition={{
                          duration: 8,
                          repeat: Infinity,
                          ease: 'linear',
                        }}
                      >
                        {/* Outer glow circle */}
                        <motion.div
                          className="absolute h-12 w-12 rounded-full"
                          style={{
                            background: 'radial-gradient(circle, var(--nt-primary) 0%, transparent 70%)',
                            filter: 'blur(8px)',
                            opacity: 0.3,
                          }}
                          animate={{
                            scale: [1, 1.2, 1],
                            opacity: [0.2, 0.4, 0.2],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: 'easeInOut',
                          }}
                        />

                        {/* Logo SVG */}
                        <div className="relative z-20 flex items-center justify-center">
                          <motion.div
                            animate={{
                              scale: [1, 1.05, 1],
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              ease: 'easeInOut',
                            }}
                            style={{ opacity: 0.9 }}
                          >
                            <svg
                              fill="none"
                              height="32"
                              viewBox="0 0 40 48"
                              width="32"
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-8 w-8"
                            >
                              <path
                                clipRule="evenodd"
                                d="m20 41.1364c1.8693 0 3.6688-.2994 5.353-.8527-1.7175-.2891-3.5271-.8718-5.3517-1.7289-1.8251.8573-3.6351 1.4402-5.3529 1.7294 1.6838.553 3.4828.8522 5.3516.8522zm-16.28276-11.7805c.28897-1.7183.87187-3.529 1.72942-5.3547-.8578-1.8258-1.44092-3.6366-1.73007-5.3551-.55352 1.6844-.85295 3.4842-.85295 5.3539 0 1.8704.29966 3.6709.8536 5.3559zm3.60425-2.0098c-.16001.4683-.29664.9297-.40971 1.382-.88774 3.5509-.23652 5.9879 1.06875 7.2932 1.30527 1.3052 3.74227 1.9565 7.29317 1.0687.4525-.1131.914-.2498 1.3824-.4098-1.7678-1.1549-3.5052-2.5591-5.1409-4.1948-1.63517-1.6352-3.03901-3.372-4.19371-5.1393zm12.67971 7.8448c-2.1428-1.1725-4.3241-2.7862-6.3647-4.8268-2.0401-2.0401-3.6535-4.2207-4.82586-6.363 1.17244-2.1428 2.78616-4.324 4.82676-6.3646 2.0404-2.0404 4.2213-3.6539 6.3638-4.8263 2.1424 1.1723 4.3231 2.7858 6.3632 4.826 2.0407 2.0407 3.6545 4.222 4.827 6.3649-1.1724 2.1424-2.7859 4.3231-4.8261 6.3633-2.0404 2.0405-4.2215 3.6541-6.3641 4.8265zm12.6793-7.8447c-1.1548 1.7674-2.5586 3.5043-4.1939 5.1395-1.6354 1.6355-3.3726 3.0395-5.1402 4.1944.4682.1599.9295.2965 1.3817.4096 3.5509.8877 5.9879.2365 7.2932-1.0688 1.3052-1.3052 1.9565-3.7422 1.0687-7.2932-.113-.4521-.2496-.9133-.4095-1.3815zm1.8748-3.345c.8572-1.8245 1.44-3.634 1.7293-5.3514.5528 1.6834.8518 3.4819.8518 5.3502 0 1.869-.2993 3.6682-.8524 5.3521-.2891-1.7172-.8718-3.5265-1.7287-5.3509zm-9.2009-16.28444c-1.7179.28914-3.528.87204-5.3531 1.72941-1.8256-.85764-3.6361-1.44069-5.3544-1.72984 1.6842-.55336 3.4837-.85269 5.3531-.85269 1.8699 0 3.6698.29949 5.3544.85312zm8.7037 2.05754c-3.6125-3.57015-8.5779-5.7743-14.0581-5.7743-11.0457 0-20 8.9543-20 20s8.9543 20 20 20c5.488 0 10.4598-2.2104 14.0735-5.7896.0232-.0225.0462-.0453.0691-.0681.023-.023.0458-.0461.0684-.0693 3.5788-3.6137 5.789-8.5853 5.789-14.073 0-5.4788-2.203-10.443-5.7715-14.0552-.028-.02903-.0564-.05785-.085-.08647-.0283-.02828-.0568-.05629-.0854-.08403zm-17.4017 1.5465c-.4688-.1602-.9307-.2971-1.3835-.4103-3.551-.8877-5.98798-.2365-7.29325 1.0688s-1.9565 3.7423-1.06876 7.2932c.11322.4529.25005.9148.41032 1.3838 1.15492-1.7679 2.55915-3.5054 4.19489-5.1411 1.6354-1.6355 3.3727-3.0396 5.1403-4.1944zm16.0244 9.3353c-1.155-1.7679-2.5593-3.5055-4.1951-5.1413-1.6352-1.6352-3.3722-3.0391-5.1396-4.1939.4686-.1601.9303-.2969 1.3829-.41 3.5509-.8877 5.9879-.2365 7.2932 1.0687 1.3052 1.3053 1.9565 3.7423 1.0687 7.2933-.1132.4527-.2499.9145-.4101 1.3832z"
                                fill="var(--nt-primary)"
                                fillRule="evenodd"
                              />
                            </svg>
                          </motion.div>
                        </div>

                        {/* Liquid metal effect layers */}
                        <motion.div
                          className="absolute h-16 w-16 rounded-full"
                          style={{
                            background: 'radial-gradient(circle, var(--nt-primary) 0%, transparent 60%)',
                            filter: 'blur(14px)',
                            opacity: 0.15,
                          }}
                          animate={{
                            rotate: [0, 360],
                            scale: [0.5, 0.6, 0.5],
                          }}
                          transition={{
                            rotate: {
                              duration: 10,
                              repeat: Infinity,
                              ease: 'linear',
                            },
                            scale: {
                              duration: 3,
                              repeat: Infinity,
                              ease: 'easeInOut',
                            },
                          }}
                        />
                        <motion.div
                          className="absolute h-16 w-16 rounded-full"
                          style={{
                            background: 'radial-gradient(circle, var(--nt-primary) 0%, transparent 60%)',
                            filter: 'blur(8px)',
                            opacity: 0.1,
                          }}
                          animate={{
                            rotate: [360, 0],
                            scale: [0.55, 0.65, 0.55],
                          }}
                          transition={{
                            rotate: {
                              duration: 12,
                              repeat: Infinity,
                              ease: 'linear',
                            },
                            scale: {
                              duration: 4,
                              repeat: Infinity,
                              ease: 'easeInOut',
                            },
                          }}
                        />
                      </motion.div>
                    </div>
                    <h1 className="mb-3 text-2xl font-bold theme-text">
                      O que você quer criar?
                    </h1>
                    <p className="text-sm theme-text-muted">
                      Descreva sua tarefa e o {agent?.name || 'Paladino'} irá lidar com o resto.{' '}
                      <span className="theme-text font-medium">Desenvolva</span> e{' '}
                      <span className="theme-text font-medium">automatize</span> seus projetos.
                    </p>
                  </div>

                  {/* ChatInput Section - Centered and Open */}
                  <div className="mx-auto mb-4 w-full">
                    <ChatInput iconOnly={true} />
                  </div>

                  {/* Quick Actions */}
                  <div className="mx-auto mt-4 flex w-full flex-wrap justify-center gap-2">
                    {EXAMPLE_ACTIONS.map((action, index) => (
                      <motion.button
                        key={index}
                        type="button"
                        className="inline-flex items-center gap-2 rounded-full border theme-border bg-[color:var(--card-bg)]/50 px-3 py-1.5 text-xs theme-text hover:bg-[color:var(--card-bg)] transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {action.icon}
                        <span>{action.text}</span>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              </div>
            ) : (
              <div className="px-3 py-2 space-y-3">
                <AnimatePresence>
                  {messages.map((message, index) => {
                    const key = `${message.id}-${index}`;
                    if (message.role === 'user') {
                      return <UserMessage key={key} message={message} />;
                    }
                    return (
                      <AgentMessage
                        key={key}
                        message={message}
                        agentName={agent?.name}
                        startTypingAnimation={startTypingAnimation}
                        stopTypingAnimation={stopTypingAnimation}
                      />
                    );
                  })}
                </AnimatePresence>
                <div ref={messagesEndRef} className="h-4" />
              </div>
            )}

            <div className="pointer-events-none sticky bottom-0 z-10 h-14 bg-gradient-to-t from-[color:var(--background)] to-transparent" />
          </div>

          {/* Chat Input - Only show when there are messages */}
          {messages.length > 0 && (
            <div className="border-t theme-border bg-[color:color-mix(in_oklab,var(--card-bg)_95%,transparent)] p-2">
              <ChatInput iconOnly={true} />
            </div>
          )}
        </>
      )}
    </motion.div>
  );
}
